/**
 * Deploy-freshness for this embedded Rumio app (apps platform contract v1.13).
 *
 * Self-contained: it registers its own listener for the host's `rumio:reload`
 * and polls /version.txt to announce `rumio:update-available` to the host (which
 * then shows its "A new version of <app> is available" banner). Drop it in and
 * call startVersionWatch() once at bootstrap — no changes to the app's other
 * iframe-bridge code are needed.
 *
 * Only acts when embedded (inside the Rumio iframe) with a build-version
 * baseline stamped into <meta name="build-version"> at build time.
 */

import { inIframe, isFromParent } from "./iframe-bridge";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_POLL_DELAY_MS = 30 * 1000;

function getBaselineVersion(): string | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLMetaElement>('meta[name="build-version"]');
  const value = el?.content?.trim() || null;
  if (!value || value === "__BUILD_VERSION__") return null;
  return value;
}

/**
 * A real version is a single short token (git short SHA or a timestamp). If
 * /version.txt is misrouted and returns the SPA index.html, this rejects it so
 * we never post a bogus "update available".
 */
function looksLikeVersion(text: string): boolean {
  return text.length > 0 && text.length <= 64 && !/[<>\s]/.test(text);
}

async function fetchServerVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/version.txt", { cache: "no-store", signal });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    return looksLikeVersion(text) ? text : null;
  } catch {
    return null;
  }
}

async function reloadToLatest(): Promise<void> {
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      /* swallow */
    }
  }
  if (typeof window !== "undefined" && "caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch {
      /* swallow */
    }
  }
  window.location.reload();
}

/**
 * Start watching for a newer deploy of this app. Returns a teardown function.
 * No-ops (apart from honoring rumio:reload) outside an iframe or without a
 * build baseline (dev / unstamped builds).
 */
export function startVersionWatch(): () => void {
  if (typeof window === "undefined") return () => {};
  if (!inIframe()) return () => {};

  // Honor the host's hard-reload request. Registered even without a baseline —
  // the host only sends this after we announced an update.
  const onMessage = (ev: MessageEvent) => {
    if (!isFromParent(ev)) return;
    const data = ev.data as { type?: unknown } | null;
    if (data && typeof data === "object" && data.type === "rumio:reload") {
      void reloadToLatest();
    }
  };
  window.addEventListener("message", onMessage);

  const baseline = getBaselineVersion();
  if (!baseline) {
    return () => window.removeEventListener("message", onMessage);
  }

  let fired = false;
  const controller = new AbortController();
  const check = async () => {
    if (fired) return;
    const server = await fetchServerVersion(controller.signal);
    if (!server) return;
    if (server !== baseline) {
      fired = true;
      window.parent?.postMessage({ type: "rumio:update-available", version: server }, "*");
    }
  };

  const initial = setTimeout(check, INITIAL_POLL_DELAY_MS);
  const interval = setInterval(check, POLL_INTERVAL_MS);
  const onVisibility = () => {
    if (document.visibilityState === "visible") void check();
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("message", onMessage);
    controller.abort();
    clearTimeout(initial);
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
